/**
 * Orthogonal Path Strategy
 *
 * Creates strict right-angle paths between elements with NO rounded corners.
 * Same elbow routing logic as RoundedElbow (shared via BasePathStrategy)
 * but with sharp corners. Uses only L (line) commands — no curves.
 */

import type { Bound } from '@blocksuite/affine/global/gfx';

import type { AnchorPosition, EdgePathType, Point } from '../../types';
import { BasePathStrategy } from './base-path-strategy';
import type { IEdgePathStrategy, PathRoutingContext } from './types';

/**
 * Orthogonal (strict right-angle) path strategy.
 *
 * Creates L-shaped or Z-shaped paths with sharp 90-degree corners.
 */
export class OrthogonalPathStrategy
  extends BasePathStrategy
  implements IEdgePathStrategy
{
  readonly type: EdgePathType = 'orthogonal';

  calculatePath(
    source: Bound,
    target: Bound,
    sourceAnchorHint?: AnchorPosition,
    targetAnchorHint?: AnchorPosition,
    routingContext?: PathRoutingContext
  ): string {
    const { sourceAnchor: s, targetAnchor: t } = this.findAnchors(
      source,
      target,
      sourceAnchorHint,
      targetAnchorHint
    );

    const sSide =
      sourceAnchorHint && sourceAnchorHint !== 'auto'
        ? sourceAnchorHint
        : this.getAnchorSide(source, s);
    const tSide =
      targetAnchorHint && targetAnchorHint !== 'auto'
        ? targetAnchorHint
        : this.getAnchorSide(target, t);

    const waypoints = this.computeElbowWaypoints(
      s,
      t,
      sSide,
      tSide,
      routingContext?.sourceObstacle ?? source,
      routingContext?.targetObstacle ?? target
    );

    let path = `M ${s.x} ${s.y}`;
    for (const wp of waypoints) {
      path += ` L ${wp.x} ${wp.y}`;
    }
    path += ` L ${t.x} ${t.y}`;
    return path;
  }

  getPathPoints(source: Bound, target: Bound, segments = 20): Point[] {
    const { sourceAnchor: s, targetAnchor: t } = this.findNearestAnchors(
      source,
      target
    );

    const sSide = this.getAnchorSide(source, s);
    const tSide = this.getAnchorSide(target, t);
    const waypoints = this.computeElbowWaypoints(
      s,
      t,
      sSide,
      tSide,
      source,
      target
    );
    return this.samplePolyline([s, ...waypoints, t], segments);
  }

  calculateSelfLoopPath(bound: Bound): string {
    return this.buildSelfLoopPath(bound);
  }

  getSelfLoopPathPoints(bound: Bound, segments = 20): Point[] {
    return this.buildSelfLoopPathPoints(bound, segments);
  }
}
